/**
 * ChatTranscript — extracted from ConversationsChat.js (Session 4 refactor).
 * Jul 2026
 *
 * Purely presentational. Owns the scrollable messages surface between the
 * AvA display panel and the composer input:
 *   • Loading state              — full-height spinner
 *   • Empty + warming-up state   — "AvA is getting ready…" splash
 *   • Empty + ready state        — "Ready to Chat" onboarding hint
 *   • Populated state            — HandoverSessionBanner (if any) +
 *                                  ChatMessageBubble per turn + typing
 *                                  bubble while sending / ava is typing
 *
 * Behaviour byte-identical to the pre-extraction inline JSX. Parent still
 * owns `messages`, `handover`, `selectedAva`, and the copy/delete actions.
 * The scroll anchor `messagesEndRef` is threaded through so parent's
 * "scroll to bottom" effect keeps working exactly as before.
 */
import React from 'react';
import { Mic } from 'lucide-react';
import ChatMessageBubble from './ChatMessageBubble';
import HandoverSessionBanner from './HandoverSessionBanner';

export const ChatTranscript = ({
  loading,
  messages,
  avaTyping,
  sending,
  selectedAva,
  interactionMode,
  revealingMsgId,
  revealedWords,
  copiedMsgId,
  setCopiedMsgId,
  onSpeak,
  setMessages,
  handover,
  navigate,
  messagesEndRef,
}) => {
  if (loading) {
    // Founder directive: the transcript pane contains ONLY messages — all
    // thinking/loading indication lives in the status slot under the AvA name.
    return <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4" />;
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4">
        <div className="h-full flex flex-col items-center justify-center text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <Mic size={32} className="text-purple-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900">Ready to Chat</h3>
          <p className="text-gray-500 mt-1">Type or use your voice to start</p>
        </div>
      </div>
    );
  }

  const onCopy = (m) => {
    navigator.clipboard.writeText(m.content);
    setCopiedMsgId(m.id);
    setTimeout(() => setCopiedMsgId(null), 2000);
  };

  const onDelete = (m) => {
    if (!window.confirm(
      'Delete this turn from the conversation? This only affects what is shown here — the practice session continues.',
    )) return;
    setMessages((prev) => prev.filter((x) => x.id !== m.id));
  };

  return (
    <div className="flex-1 overflow-y-auto p-3 sm:p-6 space-y-4">
      {/* Handover Referral Card (Directed Referral Engine) */}
      {(handover.avaMessage || handover.primaryReferral) && (
        <HandoverSessionBanner
          avaMessage={handover.avaMessage}
          primaryReferral={handover.primaryReferral}
          secondaryReferral={handover.secondaryReferral}
          internationalDirectory={handover.internationalDirectory}
          interventionId={handover.interventionId}
        />
      )}
      {messages.map((msg) => (
        <ChatMessageBubble
          key={msg.id}
          msg={msg}
          selectedAva={selectedAva}
          interactionMode={interactionMode}
          revealingMsgId={revealingMsgId}
          revealedWords={revealedWords}
          copiedMsgId={copiedMsgId}
          onSpeak={onSpeak}
          onCopy={onCopy}
          onDelete={onDelete}
          onChallengeClick={(slug) => navigate(`/challenges/${slug}`)}
        />
      ))}
      {(sending || avaTyping) && <div data-testid="ava-typing-marker" className="hidden" />}
      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatTranscript;
